
import React, { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, User, Mail, MessageSquare, Tag, Linkedin, AlertCircle, Phone, CheckCircle2, X, ShieldAlert } from 'lucide-react';

interface FormState {
  name: string;
  email: string;
  phone: string;
  _subject: string;
  message: string;
  privacyAccepted: boolean;
}

interface FormErrors {
  name?: string;
  email?: string;
  phone?: string;
  _subject?: string;
  message?: string;
  privacyAccepted?: string;
}

const ContactForm: React.FC = () => {
  const [formData, setFormData] = useState<FormState>({
    name: '',
    email: '',
    phone: '',
    _subject: '',
    message: '',
    privacyAccepted: false
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<{ [key: string]: boolean }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);

  const validateField = useCallback((name: string, value: any): string | undefined => {
    switch (name) {
      case 'name':
        if (typeof value !== 'string' || value.trim().length === 0) return "Nombre requerido";
        if (value.trim().length < 3) return "Mínimo 3 caracteres";
        if (!/^[a-zA-ZÀ-ÿ\s]{3,}$/.test(value)) return "Solo se permiten letras";
        return undefined;
      case 'email':
        if (!value) return "Email requerido";
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return "Email inválido";
        return undefined;
      case 'phone':
        if (!value) return "Teléfono requerido";
        const cleanPhone = value.replace(/\D/g, '');
        if (cleanPhone.length !== 9) return "Debe tener exactamente 9 dígitos";
        return undefined;
      case '_subject':
        if (!value || value.trim().length < 4) return "Asunto demasiado corto";
        return undefined;
      case 'message':
        if (!value || value.trim().length < 3) return "Mensaje demasiado corto (mín. 3)";
        return undefined;
      case 'privacyAccepted':
        if (!value) return "Debes aceptar la política de privacidad";
        return undefined;
      default:
        return undefined;
    }
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target as HTMLInputElement;
    let finalValue: any = type === 'checkbox' ? (e.target as HTMLInputElement).checked : value;
    
    // Restricción específica para el teléfono: solo números y máximo 9 dígitos
    if (name === 'phone') {
      const onlyNums = value.replace(/\D/g, '');
      finalValue = onlyNums.slice(0, 9);
    }

    setFormData(prev => ({ ...prev, [name]: finalValue }));
    
    const error = validateField(name, finalValue);
    setErrors(prev => ({ ...prev, [name]: error }));
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target as HTMLInputElement;
    const finalValue = type === 'checkbox' ? (e.target as HTMLInputElement).checked : value;
    
    setTouched(prev => ({ ...prev, [name]: true }));
    const error = validateField(name, finalValue);
    setErrors(prev => ({ ...prev, [name]: error }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrorMessage(null);

    const newErrors: FormErrors = {};
    let hasErrors = false;

    (Object.keys(formData) as Array<keyof FormState>).forEach((key) => {
      const error = validateField(key, formData[key]);
      if (error) {
        newErrors[key as keyof FormErrors] = error;
        hasErrors = true;
      }
    });

    setErrors(newErrors);
    setTouched(Object.keys(formData).reduce((acc, key) => ({ ...acc, [key]: true }), {}));

    if (hasErrors) return;

    setIsSubmitting(true);

    try {
      const response = await fetch("https://formspree.io/f/mgovongy", {
        method: "POST",
        body: JSON.stringify(formData),
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setSubmitted(true);
        setFormData({ name: '', email: '', phone: '', _subject: '', message: '', privacyAccepted: false });
        setErrors({});
        setTouched({});
      } else {
        const result = await response.json();
        setErrorMessage(result.errors?.[0]?.message || "Error al enviar los datos.");
      }
    } catch (error) {
      setErrorMessage("Error de red. Por favor, inténtelo de nuevo.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const getInputStatusClass = (name: keyof FormState) => {
    if (!touched[name]) return 'border-white/10 focus:border-[#39FF14]/50 focus:ring-[#39FF14]/20';
    if (errors[name]) return 'border-red-500/50 focus:border-red-500 focus:ring-red-500/20 shadow-[0_0_15px_rgba(239,68,68,0.15)]';
    return 'border-[#39FF14]/40 focus:border-[#39FF14] focus:ring-[#39FF14]/20 shadow-[0_0_15px_rgba(57,255,20,0.1)]';
  };

  const PrivacyModal = () => (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[200] flex items-center justify-center p-4 md:p-6 bg-black/90 backdrop-blur-md"
    >
      <motion.div 
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.9, y: 20 }}
        className="glass-panel w-full max-w-2xl max-h-[80vh] rounded-2xl border border-[#39FF14]/30 overflow-hidden flex flex-col"
      >
        <div className="p-6 border-b border-white/10 flex justify-between items-center bg-[#1a1a1a]">
          <div className="flex items-center gap-3 text-[#39FF14]">
            <ShieldAlert size={20} />
            <h3 className="font-syncopate text-xs md:text-sm font-bold uppercase tracking-wider">Protocolo de Privacidad</h3>
          </div>
          <button onClick={() => setShowPrivacyModal(false)} className="text-gray-500 hover:text-white transition-colors">
            <X size={20} />
          </button>
        </div>
        
        <div className="p-6 md:p-8 overflow-y-auto font-mono text-[10px] md:text-xs leading-relaxed text-gray-400 space-y-6">
          <section>
            <h4 className="text-[#39FF14] font-bold mb-2 uppercase tracking-widest">[1.0] RESPONSABLE DEL TRATAMIENTO</h4>
            <p>Identidad: Juanma Tena Luque<br />Email: juanmtena@hotmail.com</p>
          </section>
          
          <section>
            <h4 className="text-[#39FF14] font-bold mb-2 uppercase tracking-widest">[2.0] FINALIDAD</h4>
            <p>Tratamos la información que nos facilita con el fin de prestarles el servicio solicitado y realizar la facturación del mismo. Los datos proporcionados se conservarán mientras se mantenga la relación comercial o durante los años necesarios para cumplir con las obligaciones legales.</p>
          </section>
          
          <section>
            <h4 className="text-[#39FF14] font-bold mb-2 uppercase tracking-widest">[3.0] LEGITIMACIÓN</h4>
            <p>La base legal para el tratamiento de sus datos es el consentimiento del interesado al marcar la casilla de aceptación en este formulario.</p>
          </section>
          
          <section>
            <h4 className="text-[#39FF14] font-bold mb-2 uppercase tracking-widest">[4.0] DESTINATARIOS</h4>
            <p>Los datos no se cederán a terceros salvo en los casos en que exista una obligación legal o sea necesario para la prestación del servicio (proveedores de hosting/email).</p>
          </section>
          
          <section>
            <h4 className="text-[#39FF14] font-bold mb-2 uppercase tracking-widest">[5.0] DERECHOS</h4>
            <p>Usted tiene derecho a obtener confirmación sobre si estamos tratando sus datos personales, por tanto tiene derecho a acceder a sus datos personales, rectificar los datos inexactos o solicitar su supresión cuando los datos ya no sean necesarios.</p>
          </section>
        </div>
        
        <div className="p-6 border-t border-white/10 bg-[#1a1a1a]">
          <button 
            onClick={() => setShowPrivacyModal(false)}
            className="w-full py-3 border border-[#39FF14]/40 text-[#39FF14] text-[10px] font-bold uppercase tracking-[0.2em] hover:bg-[#39FF14]/10 transition-all rounded-lg"
          >
            ENTENDIDO / CERRAR
          </button>
        </div>
      </motion.div>
    </motion.div>
  );

  if (submitted) {
    return (
      <motion.div 
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="max-w-xl mx-auto text-center py-20 px-6 glass-panel rounded-2xl border border-[#39FF14]/30 shadow-[0_0_40px_rgba(57,255,20,0.1)]"
      >
        <div className="w-20 h-20 bg-[#39FF14]/20 rounded-full flex items-center justify-center mx-auto mb-6 border border-[#39FF14] relative">
          <CheckCircle2 className="text-[#39FF14]" size={40} />
          <motion.div 
            animate={{ scale: [1, 1.5, 1], opacity: [0.5, 0, 0.5] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="absolute inset-0 border border-[#39FF14] rounded-full"
          />
        </div>
        <h3 className="font-syncopate text-2xl font-bold text-[#39FF14] mb-4 uppercase tracking-tighter">MENSAJE RECIBIDO</h3>
        <p className="text-gray-400 uppercase tracking-widest text-[10px] font-bold mb-8">
          Tu mensaje ha sido enviado correctamente. Me pondré en contacto contigo pronto.
        </p>
        <button 
          onClick={() => setSubmitted(false)}
          className="text-[10px] font-bold text-white uppercase tracking-[0.3em] hover:text-[#39FF14] transition-colors border-b border-[#39FF14]/30 pb-1"
        >
          ENVIAR OTRO MENSAJE
        </button>
      </motion.div>
    );
  }

  return (
    <section id="contact-form" className="py-24 px-6 lg:px-20 max-w-7xl mx-auto">
      <AnimatePresence>
        {showPrivacyModal && <PrivacyModal />}
      </AnimatePresence>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
        <div>
          <div className="flex items-center gap-4 mb-6">
            <div className="h-[1px] w-12 bg-[#39FF14]/40" />
            <span className="text-[10px] font-bold tracking-[0.4em] uppercase text-[#39FF14]">Contacto Directo</span>
          </div>
          <h2 className="font-syncopate text-3xl md:text-5xl font-bold mb-8 uppercase leading-tight">
            ¿TIENES UN <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#39FF14] to-emerald-400">PROYECTO</span> EN MENTE?
          </h2>
          <p className="text-gray-400 max-w-md text-sm md:text-base leading-relaxed mb-10">
            Hablemos sobre tu visión. Estoy listo para ayudarte a crear una experiencia digital de alto impacto y rendimiento excepcional.
          </p>
          
          <div className="space-y-6">
            <div className="flex items-center gap-4 group">
              <div className="w-10 h-10 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center group-hover:border-[#39FF14]/50 transition-colors">
                <Mail className="text-[#39FF14]/60 group-hover:text-[#39FF14]" size={18} />
              </div>
              <div>
                <span className="block text-[10px] text-gray-500 font-bold uppercase tracking-widest">Email Principal</span>
                <a href="mailto:juanmtena@hotmail.com" className="text-sm font-bold text-gray-200 hover:text-[#39FF14] transition-colors">juanmtena@hotmail.com</a>
              </div>
            </div>
            <div className="flex items-center gap-4 group">
              <a 
                href="https://www.linkedin.com/in/juanmanueltenaluque" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="w-10 h-10 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center group-hover:border-[#39FF14]/50 transition-colors"
              >
                <Linkedin className="text-[#39FF14]/60 group-hover:text-[#39FF14]" size={18} />
              </a>
              <div>
                <span className="block text-[10px] text-gray-500 font-bold uppercase tracking-widest">LinkedIn Profile</span>
                <a href="https://www.linkedin.com/in/juanmanueltenaluque" target="_blank" rel="noopener noreferrer" className="text-sm font-bold text-gray-200 hover:text-[#39FF14] transition-colors">juanmanueltenaluque</a>
              </div>
            </div>
          </div>
        </div>

        <motion.div 
          initial={{ opacity: 0, x: 20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          className="glass-panel p-8 md:p-10 rounded-2xl border border-white/5 relative overflow-hidden"
        >
          <div className="absolute top-0 right-0 w-32 h-32 bg-[#39FF14]/5 blur-3xl pointer-events-none" />
          
          <form onSubmit={handleSubmit} noValidate className="space-y-6 relative z-10">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* NOMBRE */}
              <div className="space-y-2">
                <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500 ml-1 flex items-center gap-2">
                  <User size={12} className={errors.name && touched.name ? "text-red-500" : "text-[#39FF14]"} /> Nombre
                </label>
                <input 
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Tu nombre completo"
                  className={`w-full bg-white/5 border rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-1 transition-all placeholder:text-gray-700 ${getInputStatusClass('name')}`}
                />
                <AnimatePresence>
                  {errors.name && touched.name && (
                    <motion.p initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="text-red-500 text-[9px] font-bold uppercase pl-1">{errors.name}</motion.p>
                  )}
                </AnimatePresence>
              </div>

              {/* EMAIL */}
              <div className="space-y-2">
                <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500 ml-1 flex items-center gap-2">
                  <Mail size={12} className={errors.email && touched.email ? "text-red-500" : "text-[#39FF14]"} /> Email
                </label>
                <input 
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="tu@email.com"
                  className={`w-full bg-white/5 border rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-1 transition-all placeholder:text-gray-700 ${getInputStatusClass('email')}`}
                />
                <AnimatePresence>
                  {errors.email && touched.email && (
                    <motion.p initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="text-red-500 text-[9px] font-bold uppercase pl-1">{errors.email}</motion.p>
                  )}
                </AnimatePresence>
              </div>

              {/* TELEFONO */}
              <div className="space-y-2">
                <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500 ml-1 flex items-center gap-2">
                  <Phone size={12} className={errors.phone && touched.phone ? "text-red-500" : "text-[#39FF14]"} /> Teléfono
                </label>
                <input 
                  name="phone"
                  type="tel"
                  maxLength={9}
                  value={formData.phone}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="600000000"
                  className={`w-full bg-white/5 border rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-1 transition-all placeholder:text-gray-700 ${getInputStatusClass('phone')}`}
                />
                <AnimatePresence>
                  {errors.phone && touched.phone && (
                    <motion.p initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="text-red-500 text-[9px] font-bold uppercase pl-1">{errors.phone}</motion.p>
                  )}
                </AnimatePresence>
              </div>

              {/* ASUNTO */}
              <div className="space-y-2">
                <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500 ml-1 flex items-center gap-2">
                  <Tag size={12} className={errors._subject && touched._subject ? "text-red-500" : "text-[#39FF14]"} /> Asunto
                </label>
                <input 
                  name="_subject"
                  type="text"
                  value={formData._subject}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Ej: Presupuesto"
                  className={`w-full bg-white/5 border rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-1 transition-all placeholder:text-gray-700 ${getInputStatusClass('_subject')}`}
                />
                <AnimatePresence>
                  {errors._subject && touched._subject && (
                    <motion.p initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="text-red-500 text-[9px] font-bold uppercase pl-1">{errors._subject}</motion.p>
                  )}
                </AnimatePresence>
              </div>
            </div>

            {/* MENSAJE */}
            <div className="space-y-2">
              <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500 ml-1 flex items-center gap-2">
                <MessageSquare size={12} className={errors.message && touched.message ? "text-red-500" : "text-[#39FF14]"} /> Mensaje
              </label>
              <textarea 
                name="message"
                rows={4}
                value={formData.message}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Escribe tu mensaje aquí..."
                className={`w-full bg-white/5 border rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-1 transition-all placeholder:text-gray-700 resize-none ${getInputStatusClass('message')}`}
              />
              <AnimatePresence>
                {errors.message && touched.message && (
                  <motion.p initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="text-red-500 text-[9px] font-bold uppercase pl-1">{errors.message}</motion.p>
                )}
              </AnimatePresence>
            </div>

            {/* PRIVACIDAD CHECKBOX */}
            <div className="space-y-2">
              <div className="flex items-start gap-3">
                <div className="relative flex items-center h-5">
                  <input
                    id="privacyAccepted"
                    name="privacyAccepted"
                    type="checkbox"
                    checked={formData.privacyAccepted}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className="w-4 h-4 rounded border-[#39FF14]/40 bg-white/5 text-[#39FF14] focus:ring-[#39FF14]/20 transition-all cursor-pointer"
                  />
                </div>
                <div className="text-[10px] md:text-xs leading-none">
                  <label htmlFor="privacyAccepted" className={`font-medium uppercase tracking-widest cursor-pointer ${errors.privacyAccepted && touched.privacyAccepted ? "text-red-500" : "text-gray-400"}`}>
                    He leído y acepto la{" "}
                    <button 
                      type="button"
                      onClick={() => setShowPrivacyModal(true)}
                      className="text-[#39FF14] underline underline-offset-2 hover:text-[#39FF14]/80 transition-colors"
                    >
                      política de tratamiento de datos
                    </button>
                  </label>
                </div>
              </div>
              <AnimatePresence>
                {errors.privacyAccepted && touched.privacyAccepted && (
                  <motion.p initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="text-red-500 text-[9px] font-bold uppercase pl-7">{errors.privacyAccepted}</motion.p>
                )}
              </AnimatePresence>
            </div>

            {/* ERROR GENERAL API */}
            {errorMessage && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="flex items-start gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                <AlertCircle className="text-red-500 shrink-0" size={14} />
                <p className="text-red-500 text-[9px] font-bold uppercase tracking-widest">{errorMessage}</p>
              </motion.div>
            )}

            {/* BOTON ENVIAR */}
            <motion.button
              whileHover={{ scale: 1.02, boxShadow: "0 0 30px rgba(57, 255, 20, 0.4)" }}
              whileTap={{ scale: 0.98 }}
              disabled={isSubmitting}
              type="submit"
              className="w-full py-4 bg-[#39FF14] text-black font-bold uppercase tracking-[0.2em] text-xs rounded-lg shadow-[0_0_20px_rgba(57,255,20,0.3)] transition-all flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <div className="flex items-center gap-2">
                  <span className="animate-pulse">ENVIANDO...</span>
                  <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                </div>
              ) : (
                <>ENVIAR <Send size={16} /></>
              )}
            </motion.button>
          </form>
        </motion.div>
      </div>
    </section>
  );
};

export default ContactForm;
